import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { emailTrackingService } from '../../../services/emailTrackingService';

const FailureAnalysis = ({ emailLogs }) => {
  const [batchRetrying, setBatchRetrying] = useState(false);

  const failureStats = useMemo(() => {
    const failed = emailLogs?.filter(log => 
      log?.status === 'failed' || log?.status === 'bounced'
    ) || [];

    const bounced = failed?.filter(log => log?.status === 'bounced');
    const hardBounces = bounced?.filter(log => log?.bounceType === 'hard')?.length || 0;
    const softBounces = bounced?.filter(log => log?.bounceType === 'soft')?.length || 0;

    // Count retryable failures
    const retryable = failed?.filter(log => 
      log?.bounceType !== 'hard' && 
      (!log?.errorCode || !['invalid_email', 'invalid_from_address']?.includes(log?.errorCode))
    )?.length || 0;

    // Group by error message
    const errorGroups = {};
    failed?.forEach(log => {
      const errorKey = log?.errorMessage || 'Unknown error';
      if (!errorGroups?.[errorKey]) {
        errorGroups[errorKey] = {
          message: errorKey,
          count: 0,
          emails: []
        };
      }
      errorGroups[errorKey].count++;
      errorGroups?.[errorKey]?.emails?.push(log?.recipientEmail);
    });

    const topErrors = Object.values(errorGroups)?.sort((a, b) => b?.count - a?.count)?.slice(0, 5);

    return {
      totalFailed: failed?.length,
      totalBounced: bounced?.length,
      hardBounces,
      softBounces,
      retryable,
      topErrors,
      recentFailures: failed?.slice(0, 10)
    };
  }, [emailLogs]);

  const handleBatchRetry = async () => {
    if (batchRetrying) return;

    const confirmed = window.confirm(
      `This will retry ${failureStats?.retryable} eligible failed emails. Continue?`
    );

    if (!confirmed) return;

    try {
      setBatchRetrying(true);
      const result = await emailTrackingService?.triggerBatchRetry(50);
      
      if (result?.success) {
        alert(`Batch retry completed!\n\nProcessed: ${result?.processed}\nSucceeded: ${result?.succeeded}\nFailed: ${result?.failed}`);
      } else {
        alert('Batch retry failed. Please try again.');
      }
    } catch (error) {
      console.error('Error triggering batch retry:', error);
      alert(`Failed to trigger batch retry: ${error?.message}`);
    } finally {
      setBatchRetrying(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      failed: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Failed' },
      bounced: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Bounced' }
    };
    const badge = badges?.[status] || badges?.failed;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge?.bg} ${badge?.text}`}>
        {badge?.label}
      </span>
    );
  };

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-xl shadow-elevation-1 p-6 mb-8 border border-white/20">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
            <Icon name="AlertCircle" size={20} className="text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">Failure Analysis</h3>
            <p className="text-sm text-gray-300">Detailed breakdown of delivery issues</p>
          </div>
        </div>
        {failureStats?.retryable > 0 && (
          <button
            onClick={handleBatchRetry}
            disabled={batchRetrying}
            className="px-4 py-2 bg-[#1FB5A5] hover:bg-[#1a9d8f] disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors flex items-center gap-2"
          >
            {batchRetrying ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Icon name="RefreshCw" size={16} />
                Retry All Eligible ({failureStats?.retryable})
              </>
            )}
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Failure Summary */}
        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
          <h4 className="text-sm font-semibold text-gray-300 mb-4">Failure Summary</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm">Total Failed</span>
              <span className="text-white font-bold text-lg">{failureStats?.totalFailed}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm">Total Bounced</span>
              <span className="text-white font-bold text-lg">{failureStats?.totalBounced}</span>
            </div>
            <div className="h-px bg-white/10 my-2"></div>
            <div className="flex items-center justify-between">
              <span className="text-orange-400 text-sm">Hard Bounces</span>
              <span className="text-orange-400 font-semibold">{failureStats?.hardBounces}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-yellow-400 text-sm">Soft Bounces</span>
              <span className="text-yellow-400 font-semibold">{failureStats?.softBounces}</span>
            </div>
            <div className="h-px bg-white/10 my-2"></div>
            <div className="flex items-center justify-between">
              <span className="text-[#1FB5A5] text-sm font-medium">Retryable</span>
              <span className="text-[#1FB5A5] font-bold text-lg">{failureStats?.retryable}</span>
            </div>
          </div>
        </div>

        {/* Top Error Messages */}
        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
          <h4 className="text-sm font-semibold text-gray-300 mb-4">Top Error Messages</h4>
          {failureStats?.topErrors?.length > 0 ? (
            <div className="space-y-3">
              {failureStats?.topErrors?.map((error, index) => (
                <div key={index} className="border-b border-white/10 pb-2 last:border-0">
                  <div className="flex items-start justify-between mb-1">
                    <span className="text-red-400 text-xs font-medium">#{index + 1}</span>
                    <span className="text-white font-semibold">{error?.count}</span>
                  </div>
                  <p className="text-gray-400 text-xs line-clamp-2">{error?.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Icon name="CheckCircle" size={32} className="text-green-400 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">No errors detected</p>
            </div>
          )}
        </div>

        {/* Recent Failures */}
        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
          <h4 className="text-sm font-semibold text-gray-300 mb-4">Recent Failures</h4>
          {failureStats?.recentFailures?.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
              {failureStats?.recentFailures?.map((log, index) => (
                <div key={index} className="bg-white/5 rounded p-2 border border-white/10">
                  <div className="flex items-center justify-between mb-1">
                    {getStatusBadge(log?.status)}
                    <span className="text-xs text-gray-400">
                      {new Date(log?.createdAt)?.toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-white text-xs font-medium truncate">{log?.recipientEmail}</p>
                  <div className="flex items-center justify-between mt-1">
                    {log?.bounceType && (
                      <span className="text-xs text-orange-400">{log?.bounceType} bounce</span>
                    )}
                    {log?.retryCount > 0 && (
                      <span className="text-xs text-yellow-400">{log?.retryCount} retries</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Icon name="CheckCircle" size={32} className="text-green-400 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">No recent failures</p>
            </div>
          )}
        </div>
      </div>
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(31, 181, 165, 0.5);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(31, 181, 165, 0.7);
        }
      `}</style>
    </div>
  );
};

export default FailureAnalysis;